import { uiSurface } from "shared/ui/uiSurface"
import { LocalOffer } from "@mui/icons-material"
import { Box, CircularProgress, Paper, Stack, Typography } from "@mui/material"
import { FC, useCallback, useState } from "react"
import { useTranslation } from "react-i18next"
import {
  ClientTag,
  useGetTagsQuery,
  useLinkClientTagMutation,
  useUnlinkClientTagMutation,
} from "../../apis/appApi"
import { TagSelector } from "../orders/create/TagSelector"

type ClientTagSectionProps = {
  clientId: number
  clientTags: ClientTag[]
  onSuccess: (message: string) => void
}

export const ClientTagSection: FC<ClientTagSectionProps> = ({
  clientId,
  clientTags,
  onSuccess,
}) => {
  const { t } = useTranslation()
  const { data: availableTags, isLoading: tagsLoading } = useGetTagsQuery({ type: "CLIENT" })
  const [linkTag, { isLoading: isLinking }] = useLinkClientTagMutation()
  const [unlinkTag, { isLoading: isUnlinking }] = useUnlinkClientTagMutation()
  const [pendingTagId, setPendingTagId] = useState<number | null>(null)

  const isBusy = isLinking || isUnlinking

  const selectedTagIds = clientTags.map((ct) => ct.tagId)

  const handleTagToggle = useCallback(
    async (tagId: number) => {
      if (isBusy) return
      setPendingTagId(tagId)

      try {
        const isCurrentlySelected = selectedTagIds.includes(tagId)
        if (isCurrentlySelected) {
          await unlinkTag({ clientId, tagId }).unwrap()
          onSuccess(t("clients.tags.unlinked"))
        } else {
          await linkTag({ clientId, tagId }).unwrap()
          onSuccess(t("clients.tags.linked"))
        }
      } catch {
        // Error handled by RTK Query
      } finally {
        setPendingTagId(null)
      }
    },
    [isBusy, selectedTagIds, clientId, linkTag, unlinkTag, onSuccess, t],
  )

  const mappedTags = (availableTags || []).map((tag: any) => ({
    id: tag.id,
    label: tag.label,
    color: tag.color,
    icon: tag.icon,
    shortLabel: tag.shortLabel,
    displayOption: tag.displayOption,
  }))

  return (
    <Paper {...uiSurface("UI-DLV2-092")} variant="outlined" sx={{ p: 2, bgcolor: "grey.50" }}>
      <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1.5 }}>
        <Box sx={{ color: "text.secondary", display: "flex" }}>
          <LocalOffer fontSize="small" />
        </Box>
        <Typography variant="subtitle2" fontWeight={600}>
          {t("clients.tags.title")}
        </Typography>
        <Typography variant="caption" color="text.secondary">
          ({clientTags.length})
        </Typography>
        {isBusy && (
          <CircularProgress size={14} sx={{ ml: 0.5 }} />
        )}
      </Stack>

      {tagsLoading ? (
        <CircularProgress size={20} />
      ) : mappedTags.length === 0 ? (
        <Typography variant="body2" color="text.disabled">
          {t("clients.tags.noAvailable")}
        </Typography>
      ) : (
        <Box sx={{ opacity: pendingTagId !== null ? 0.7 : 1, transition: "opacity 0.15s" }}>
          <TagSelector
            selectedTagIds={selectedTagIds}
            availableTags={mappedTags}
            onChange={handleTagToggle}
          />
        </Box>
      )}
    </Paper>
  )
}
